import { useToast } from '@chakra-ui/react';
import { useSubmissionData } from '../contexts/SubmissionDataProvider';
import { IBlockedReviewer } from '@publishing-engine/domain/views';
import { IInviteReviewer } from '../components/Submission/ReviewTab/InviteReviewer/commonTypes';
import { IReviewer } from '@publishing-engine/domain/models';
import {
  findBlockedReviewers,
  findDuplicateIndices,
  getBlockedReviewerErrorMessage,
  handleEmptyReviewer,
  categorizeDuplicateReviewers
} from '../utils/ReviewersValidations';
import { validatorForAddReviewer } from '../api/queries';
import { DUPLICATE_REVIEWER_INVITE_ERROR_MESSAGES } from '../constants';

export const useReviewerValidation = (
  setError: any,
  blockedReviewers: IBlockedReviewer[],
  fieldName: string = 'reviewers',
  requireUserExistence: boolean = false
) => {
  const toast = useToast();
  const { submissionId, submissionContextId } = useSubmissionData();
  const validateReviewers = async (
    reviewers: IInviteReviewer[],
    existingReviewers: IReviewer[] | Pick<IReviewer, 'email'>[]
  ) => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    const opposedList: string[] = [];
    // --- STEP 1: required + email pattern + duplicate checks ---
    reviewers.forEach((r, index) => {
      const fieldPath = `${fieldName}.${index}.email`;
      const raw = r?.email ?? '';
      const email = raw.trim();

      // required
      if (!email) {
        setError(fieldPath, {
          type: 'required',
          message: 'Email is required'
        });
        return;
      }

      // invalid email format
      if (!emailRegex.test(email)) {
        setError(fieldPath, {
          type: 'pattern',
          message: 'Please enter a valid email address'
        });
        return;
      }

      // duplicates (same logic as validateOnBlur)
      const duplicateIndices = findDuplicateIndices(
        reviewers,
        existingReviewers
      );
      if (duplicateIndices.includes(index)) {
        setError(fieldPath, {
          type: 'duplicate',
          message: DUPLICATE_REVIEWER_INVITE_ERROR_MESSAGES.REVIEWER
        });
        return;
      }
    });

    // --- STEP 2: async validation (same logic as validateOnBlur) ---
    const isEmptySingleRow =
      reviewers.length === 1 && !reviewers?.[0]?.email?.trim();

    if (!isEmptySingleRow) {
      const validation = await validatorForAddReviewer(
        submissionContextId,
        submissionId,
        reviewers.map(r => r.email.trim())
      );

      if (!validation?.length) {
        toast({
          variant: 'error',
          title: 'Error validating reviewers',
          description: 'Please try again later.'
        });
        return;
      }

      validation.forEach((item, index) => {
        const fieldPath = `${fieldName}.${index}.email`;
        setError(fieldPath, {
          type: 'duplicate',
          message: item.error.join(', ')
        });
      });
    }
    return opposedList;
  };

  const validateBulkReviewers = async (
    reviewers: IInviteReviewer[],
    existingReviewers: IReviewer[] | Pick<IReviewer, 'email'>[]
  ) => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    let hasBlockingErrors = false;
    const patternErrors: { index: number; email: string }[] = [];

    const normalized = reviewers.map((r, i) => {
      const raw = r?.email ?? '';
      const email = raw.replace(/[\n\r]/g, '').trim();

      if (!email) {
        hasBlockingErrors = true;
        setError(fieldName, {
          type: 'required',
          message:
            'Each line must contain a valid email address. Please remove any empty lines.'
        });
        return { ...r, email };
      }

      if (!emailRegex.test(email)) {
        hasBlockingErrors = true;
        patternErrors.push({ index: i, email });
        return { ...r, email };
      }

      return { ...r, email };
    });

    if (patternErrors.length > 0) {
      const list = patternErrors.map(e => `${e.email}`).join(',\n');

      setError(fieldName, {
        type: 'pattern',
        message: `These emails are invalid:\n${list}.\nReview and correct them so each line matches yourname@example.com.`
      });
    }

    // 2) Duplicate checks with categorization
    const duplicateCategories = categorizeDuplicateReviewers(
      normalized,
      existingReviewers
    );
    const allErrors: Record<string, string> = {};

    if (Object.keys(duplicateCategories).length > 0) {
      hasBlockingErrors = true;

      Object.entries(duplicateCategories).forEach(
        ([email, { isInputDuplicate, isAlreadyInvited }]) => {
          let message: string;

          if (isInputDuplicate && isAlreadyInvited) {
            // Case 3: Both duplicate in input AND already invited
            message =
              'Duplicate email IDs added, clear the duplicates to continue. The invitation for the email ID(s) entered is already sent, reinvite them or manage them in the reviews tab.';
          } else if (isInputDuplicate) {
            // Case 1: Only duplicate in input
            message =
              'Duplicate email IDs added, clear the duplicates to continue.';
          } else if (isAlreadyInvited) {
            // Case 2: Only already invited
            message =
              'This reviewer has already been added. reinvite them or manage them in the reviews tab';
          } else {
            // Fallback - should never happen
            return;
          }

          allErrors[email] = message;
        }
      );
    }

    // 3) Blocked reviewers
    const blocked = findBlockedReviewers(normalized, blockedReviewers);

    if (blocked.length > 0) {
      hasBlockingErrors = true;

      blocked.forEach(({ index, blockReason }) => {
        const email = normalized[index]?.email;
        if (email) {
          allErrors[email] = getBlockedReviewerErrorMessage(blockReason, email);
        }
      });
    }

    // Set all errors at once
    if (Object.keys(allErrors).length > 0) {
      setError(fieldName, {
        types: allErrors
      });
    }

    // 4) If any blocking validation failed → DO NOT CALL API
    if (hasBlockingErrors) return;

    // 5) Skip async API when only 1 empty row
    const skipAsync =
      normalized.length === 1 && !normalized?.[0]?.email?.trim();

    if (skipAsync) return;

    // 6) Call async API ONLY when everything above passed
    const validation = await validatorForAddReviewer(
      submissionContextId,
      submissionId,
      normalized.map(r => r.email)
    );

    if (!validation?.length) {
      toast({
        variant: 'error',
        title: 'Error validating reviewers',
        description: 'Please try again later.'
      });
      return;
    }

    // 7) Apply async results
    const errorItems = validation.filter(
      item => item.error && item.error.length > 0
    );

    // 8) Check for non-existing or invited users if required (only for auto-assign flow)
    const nonExistingUsers = requireUserExistence
      ? validation.filter(item => !item.isUserExist)
      : [];

    const invitedUsers = requireUserExistence
      ? validation.filter(item => item.userStatus === 'invited')
      : [];

    const deactivatedUsers = validation.filter(
      item => item.userStatus === 'deactivated'
    );

    const combinedErrors: Record<string, string> = {};

    if (errorItems.length > 0) {
      errorItems.forEach(item => {
        combinedErrors[item.email] = item.error
          .map(err => err.message)
          .join(', ');
      });
    }

    if (nonExistingUsers.length > 0) {
      nonExistingUsers.forEach(item => {
        combinedErrors[item.email] =
          'User does not exist. Please create the user and assign the review';
      });
    }

    if (invitedUsers.length > 0) {
      invitedUsers.forEach(item => {
        combinedErrors[item.email] =
          'Account creation pending from user. You cannot assign at the moment.';
      });
    }

    if (deactivatedUsers.length > 0) {
      deactivatedUsers.forEach(item => {
        combinedErrors[item.email] = 'This account has been deactivated.';
      });
    }

    if (Object.keys(combinedErrors).length > 0) {
      setError(fieldName, {
        types: combinedErrors
      });
    }

    return validation;
  };

  return {
    validateReviewers,
    validateBulkReviewers,
    handleEmptyReviewer
  };
};
